import { Classes, Icon, Radio, RadioGroup, Switch } from "@blueprintjs/core";
import { Tooltip2 } from "@blueprintjs/popover2";
import { format, isAfter, startOfDay } from "date-fns";
import { ButtonContainer, IDataTableColumn, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { DashboardApi, DashboardSuiviSiegeLieuSearch, SuiviSiegeLieuGridDto } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { CustomBulletList, LinkButton, SearchTablePage, ViewButton } from "../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

const StyledSwitch = styled(Switch)`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

export interface IDashboardSuivisSiegeLieuProps {}

export const DashboardSuivisSiegeLieu: React.FunctionComponent<IDashboardSuivisSiegeLieuProps> = props => {
  const { t } = useTl();
  const { user } = useAuth();
  const { theme } = useTheme();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();

  const [myDashboard, setMyDashboard] = React.useState(true);
  const [displayHidden, setDisplayHidden] = React.useState(false);
  const searchFunction = React.useCallback(
    (sObj?: DashboardSuiviSiegeLieuSearch) => {
      sObj.hideMasquees = !displayHidden;
      sObj.onlyUserDashboard = myDashboard;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchSuivisSiegeLieu({ DashboardSuiviSiegeLieuSearch: sObj });
    },
    [apiFactory, displayHidden, myDashboard]
  );

  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const { dispatchEvent } = useEventsContext();
  const marquerCommeFait = React.useCallback(
    async (idsuiviSiegeLieu: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MarquerSuiviCommeFaitConfirmTitle),
        message: t(ETLCodes.MarquerSuiviCommeFaitConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idsuiviSiegeLieu);
        await api.dashboardMarquerSuiviSiegeLieuFait({
          idsuiviSiegeLieu
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_SuivisSL");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );
  const masquerSuivi = React.useCallback(
    async (idsuiviSiegeLieu: number) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerSuiviConfirmTitle),
        message: t(ETLCodes.MasquerSuiviConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading(idsuiviSiegeLieu);
        await api.dashboardMasquerSuiviSiegeLieu({
          idsuiviSiegeLieu
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_SuivisSL");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const getActionValue = React.useCallback((row: SuiviSiegeLieuGridDto) => {
    if (!row.actionDateRealisation && !row.actionDateEcheance) return null;
    return format(!!row.actionDateRealisation ? row.actionDateRealisation : row.actionDateEcheance, "dd-MM-yyyy");
  }, []);

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage } = useTabMessage(identifier);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: SuiviSiegeLieuGridDto) => (
          <ButtonContainer>
            {user.iduser === row.realisateurAction && (
              <ToolTipButton
                tooltipPosition="bottom"
                minimal
                icon="tick"
                onClick={() => marquerCommeFait(row.idsuivi)}
                loading={!!rowLoading && rowLoading === row.idsuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
                tooltip={t(ETLCodes.MarquerCommeFait)}
              />
            )}
            {!row.masquee && !!row.actionDateEcheance && isAfter(new Date(), row.actionDateEcheance) && (
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerSuivi)}
                minimal
                icon="cross"
                onClick={() => masquerSuivi(row.idsuivi)}
                loading={!!rowLoading && rowLoading === row.idsuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
              />
            )}
            <ViewButton
              minimal={true}
              onClick={() =>
                openTabThenSendMessage(
                  !!row.idlieuFormation
                    ? `#${ERoutes.lieuFormation}/${row.idlieuFormation}/suivi/view/${row.idsuivi}`
                    : `#${ERoutes.siegeSocial}/${row.idsiegeSocial}/suivi/view/${row.idsuivi}`,
                  "FromDashboard",
                  !!row.idlieuFormation && [{ key: "idsiegeSocial", value: row.idsiegeSocial.toString() }]
                )
              }
            />
          </ButtonContainer>
        )
      },
      {
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.ActionARealiser)}</span>
            <Tooltip2
              content={
                <CustomBulletList
                  title={t(ETLCodes.Legende)}
                  items={[
                    {
                      text: t(ETLCodes.ActionRealisee),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.DateEcheanceDepassee),
                      color: theme.dangerColor
                    },
                    {
                      text: t(ETLCodes.ActionNonRealisee),
                      color: theme.warningColor
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon icon="info-sign" style={{ cursor: "pointer" }} />
            </Tooltip2>
          </LegendHeaderContainer>
        ),
        fieldName: "actionARealiser",
        render: (row: SuiviSiegeLieuGridDto) => (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: !!row.actionDateRealisation
                ? theme.sucessColor
                : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                ? theme.dangerColor
                : theme.warningColor
            }}
          >
            <Icon
              style={{ marginRight: ".5rem" }}
              icon={
                !!row.actionDateRealisation
                  ? "tick"
                  : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                  ? "issue"
                  : "time"
              }
            />
            {getActionValue(row)}
          </span>
        )
      },
      {
        header: () => t(ETLCodes.DateSuivi),
        fieldName: "dateSuivi"
      },
      {
        header: () => t(ETLCodes.RealisateurSuivi),
        fieldName: "realisateurSuivi",
        render: (row: SuiviSiegeLieuGridDto) => (
          <LinkButton
            text={row.realisateurSuivi}
            onClick={() => window.open(`#${ERoutes.users}/${row.idrealisateurSuivi}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.Encodeur),
        fieldName: "encodeur",
        render: (row: SuiviSiegeLieuGridDto) => (
          <LinkButton
            text={row.encodeur}
            onClick={() => window.open(`#${ERoutes.users}/${row.idencodeur}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.SiegeSocial),
        fieldName: "siegeSocial",
        render: (row: SuiviSiegeLieuGridDto) => (
          <LinkButton
            text={row.siegeSocial}
            onClick={() => window.open(`#${ERoutes.siegeSocial}/${row.idsiegeSocial}/detail/view`, "_blank")}
          />
        )
      },
      {
        header: () => t(ETLCodes.LieuFormation),
        fieldName: "lieuFormation",
        render: (row: SuiviSiegeLieuGridDto) => (
          <LinkButton
            text={row.lieuFormation}
            onClick={() =>
              window.open(
                `#${ERoutes.lieuFormation}/${row.idlieuFormation}/detail/view?idsiegeSocial=${row.idsiegeSocial}`,
                "_blank"
              )
            }
          />
        )
      },
      {
        header: () => t(ETLCodes.TypeSuivi),
        fieldName: "typeSuivi"
      }
    ],
    [
      getActionValue,
      marquerCommeFait,
      masquerSuivi,
      openTabThenSendMessage,
      rowLoading,
      t,
      theme.dangerColor,
      theme.sucessColor,
      theme.warningColor,
      user.iduser
    ]
  );

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetSuiviSiegeLieuCriterias();
  }, [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  return (
    <>
      <RadioGroup
        selectedValue={myDashboard ? "1" : "0"}
        inline
        onChange={e => setMyDashboard(e.currentTarget.value === "1")}
      >
        <Radio label={t(ETLCodes.ActionsQuiMeSontAssignees)} value={"1"}></Radio>
        <Radio label={t(ETLCodes.ActionsAssigneesServices)} value={"0"}></Radio>
      </RadioGroup>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        sortKeys={{ dateSuivi: "DESC" }}
        rightElement={
          <StyledSwitch
            color={theme.primaryColor}
            label={t(ETLCodes.AfficherMasquees)}
            checked={displayHidden}
            onChange={e => {
              setDisplayHidden(e.currentTarget.checked);
            }}
          ></StyledSwitch>
        }
      />
    </>
  );
};
